import { useState, useEffect, useCallback } from 'react';
import { useGameStore } from '../stores/gameStore';
import { gameApi } from '../services/api';
import { NodeAccessLevel, HackedNode } from '../types';
import { requestMidgameAd, requestRewardedAd, useAdsEnabled } from '../services/crazygames';

const ACCESS_LABEL: Record<NodeAccessLevel, { label: string; color: string }> = {
  [NodeAccessLevel.Full]:    { label: 'FULL',    color: '#4ade80' },
  [NodeAccessLevel.SshOnly]: { label: 'SSH',     color: '#60a5fa' },
  [NodeAccessLevel.FtpOnly]: { label: 'FTP',     color: '#fbbf24' },
  [NodeAccessLevel.None]:    { label: 'NONE',    color: '#555' },
};

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function HackedNodes() {
  const loadPlayer = useGameStore(s => s.loadPlayer);
  const adsEnabled = useAdsEnabled();
  const [hackedNodes, setHackedNodes] = useState<HackedNode[] | null>(null);
  const [collecting, setCollecting] = useState(false);
  const [collectMsg, setCollectMsg] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const loadHackedNodes = useCallback(async () => {
    try {
      const res = await gameApi.getHackedNodes();
      setHackedNodes((res.data ?? []) as HackedNode[]);
    } catch {
      setHackedNodes([]);
    }
  }, []);

  // Load on mount so a direct page load / F5 refresh works. Hacked nodes now live
  // in a dedicated container, fetched via their own endpoint.
  useEffect(() => { loadPlayer(); loadHackedNodes(); }, [loadPlayer, loadHackedNodes]);

  // 1s clock so in-progress infection ETAs count down.
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);

  const infectedCount = hackedNodes?.filter(n => n.infectedVersion != null).length ?? 0;

  // Normal collect. On CrazyGames, fires an interstitial (rate-limited to once per
  // 5 min) only when credits were actually collected — never nags on an empty collect.
  const handleCollect = async () => {
    setCollecting(true);
    setCollectMsg(null);
    try {
      const res = await gameApi.collectInfections();
      const collected = (res.data as { collected: number }).collected ?? 0;
      await loadPlayer();
      await loadHackedNodes();
      setCollectMsg(collected > 0 ? `+${collected.toLocaleString()} cr collected from infections.` : 'No infection credits ready yet.');
      if (collected > 0) void requestMidgameAd();
    } catch {
      setCollectMsg('Failed to collect infection credits.');
    } finally {
      setCollecting(false);
    }
  };

  // Rewarded-ad collect (CrazyGames only): peek at how much is collectable first, so we
  // only bother the player with an ad when there's actually something to collect. Watch
  // the ad → +20% bonus; if the ad fails/unavailable, collect at the base rate anyway.
  const handleCollectWithAds = async () => {
    setCollecting(true);
    setCollectMsg(null);
    try {
      let pending = 0;
      try {
        const peek = await gameApi.getInfectionsPending();
        pending = (peek.data as { pending: number }).pending ?? 0;
      } catch { /* peek best-effort; fall through to a plain collect */ }

      if (pending <= 0) {
        await loadPlayer();
        await loadHackedNodes();
        setCollectMsg('No infection credits ready yet.');
        return;
      }

      const watched = await requestRewardedAd();
      const res = await gameApi.collectInfections(watched);
      const collected = (res.data as { collected: number }).collected ?? 0;
      await loadPlayer();
      await loadHackedNodes();
      setCollectMsg(
        collected > 0
          ? `+${collected.toLocaleString()} cr collected from infections${watched ? ' (+20% ad bonus)' : ''}.`
          : 'No infection credits ready yet.',
      );
    } catch {
      setCollectMsg('Failed to collect infection credits.');
    } finally {
      setCollecting(false);
    }
  };

  if (hackedNodes === null) {
    return (
      <div className="container">
        <p style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>Loading...</p>
      </div>
    );
  }

  // Sort newest hack first; exclude entries with no access (failed previous hack cleared by ip change)
  const nodes = [...hackedNodes]
    .filter(n => n.accessLevel !== NodeAccessLevel.None)
    .sort((a, b) => new Date(b.hackedAt).getTime() - new Date(a.hackedAt).getTime());

  const npcs    = nodes.filter(n => n.isNpc);
  const players = nodes.filter(n => !n.isNpc);

  return (
    <div className="container" style={{ maxWidth: '860px' }}>
      {/* Infection-credits collector — kept outside the table, top-left. */}
      {(infectedCount > 0 || collectMsg) && (
        <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          {infectedCount > 0 && (
            <button
              onClick={handleCollect}
              disabled={collecting}
              style={{
                background: '#2e1065', color: '#c4b5fd', border: '1px solid #7c3aed',
                borderRadius: '4px', padding: '0.45rem 0.9rem', cursor: collecting ? 'not-allowed' : 'pointer',
                fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 'bold',
              }}
            >
              {collecting ? '…' : `Collect infection credits (${infectedCount})`}
            </button>
          )}
          {infectedCount > 0 && adsEnabled && (
            <button
              onClick={handleCollectWithAds}
              disabled={collecting}
              style={{
                background: '#1e3a8a', color: '#bfdbfe', border: '1px solid #3b82f6',
                borderRadius: '4px', padding: '0.45rem 0.9rem', cursor: collecting ? 'not-allowed' : 'pointer',
                fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 'bold',
              }}
            >
              {collecting ? '…' : 'Collect (ADS) +20%'}
            </button>
          )}
          {collectMsg && (
            <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#c4b5fd' }}>
              {collectMsg}
            </span>
          )}
        </div>
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="card-title" style={{ fontFamily: 'monospace' }}>Hacked Nodes</h1>
            <p style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.85rem', margin: 0 }}>
              Systems you have compromised. If a target changes their IP, access is lost.
            </p>
          </div>
          <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            {nodes.length} node{nodes.length !== 1 ? 's' : ''}
          </span>
        </div>

        {nodes.length === 0 ? (
          <div style={{
            fontFamily: 'monospace',
            color: 'var(--text-muted)',
            padding: '2.5rem',
            textAlign: 'center',
            borderTop: '1px solid var(--border-color)',
          }}>
            No compromised nodes yet. Hack a target to gain access.
          </div>
        ) : (
          <>
            {players.length > 0 && (
              <Section title="Players" rows={players} now={now} />
            )}
            {npcs.length > 0 && (
              <Section title="NPCs" rows={npcs} now={now} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function fmtEta(sec: number): string {
  return sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

function Section({ title, rows, now }: { title: string; rows: HackedNode[]; now: number }) {
  return (
    <>
      <div style={{
        padding: '0.4rem 0.75rem',
        background: '#0d0d0d',
        borderTop: '1px solid var(--border-color)',
        borderBottom: '1px solid var(--border-color)',
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        color: 'var(--text-muted)',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}>
        {title}
      </div>
      <div className="table-wrap">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
            {['Username', 'IP Address', 'Access Level', 'Infected', 'Hacked At'].map(h => (
              <th key={h} style={{
                padding: '0.45rem 0.75rem',
                textAlign: 'left',
                color: 'var(--text-muted)',
                fontSize: '0.76rem',
                fontWeight: 'normal',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(n => {
            const acc = ACCESS_LABEL[n.accessLevel] ?? ACCESS_LABEL[NodeAccessLevel.None];
            return (
              <tr key={n.ip} style={{ borderBottom: '1px solid #111' }}>
                <td style={{ padding: '0.5rem 0.75rem', color: '#e2e8f0' }}>
                  {n.username || '—'}
                </td>
                <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {n.connectionLost
                    ? <span style={{ color: '#fbbf24' }}>Connection lost</span>
                    : <span style={{ color: '#4ade80' }}>{n.ip}</span>}
                </td>
                <td style={{ padding: '0.5rem 0.75rem' }}>
                  <span style={{
                    color:        acc.color,
                    border:       `1px solid ${acc.color}`,
                    borderRadius: '3px',
                    padding:      '0.1rem 0.4rem',
                    fontSize:     '0.72rem',
                    fontWeight:   'bold',
                    letterSpacing:'0.05em',
                  }}>
                    {acc.label}
                  </span>
                </td>
                <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.82rem' }}>
                  {(() => {
                    if (n.infectedVersion == null) return <span style={{ color: '#444' }}>—</span>;
                    const remainSec = n.infectionActiveAt ? Math.ceil((new Date(n.infectionActiveAt).getTime() - now) / 1000) : 0;
                    if (remainSec > 0) return <span style={{ color: '#fbbf24' }}>infecting… ~{fmtEta(remainSec)}</span>;
                    return <span style={{ color: '#c4b5fd', fontWeight: 'bold' }}>v{n.infectedVersion.toFixed(1)}</span>;
                  })()}
                </td>
                <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                  <span title={fmtDate(n.hackedAt)}>{fmtRelative(n.hackedAt)}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </>
  );
}
